import { useState } from "react";
import { Phone, Mail, MapPin, Send, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const COMPANY_EMAIL = "divyamukesh_mukeshdivya@yahoo.co.in";

export default function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | success

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const subject = encodeURIComponent(
      `New Inquiry from ${form.name}${form.company ? ` - ${form.company}` : ""}`
    );
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone || "N/A"}\nCompany: ${form.company || "N/A"}\n\nMessage:\n${form.message}`
    );

    window.location.href = `mailto:${COMPANY_EMAIL}?subject=${subject}&body=${body}`;

    setStatus("success");
    setForm({ name: "", email: "", phone: "", company: "", message: "" });
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-32 px-6 md:px-12 max-w-7xl mx-auto"
    >
      {/* Section Header */}
      <div className="mb-16 text-center">
        <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#A07570]">
          Get in Touch
        </span>
        <h2
          data-testid="contact-heading"
          className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-[#040404] mt-3"
        >
          Let's discuss your
          <br />
          requirements
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
        {/* Contact Form */}
        <div className="bg-white p-8 md:p-12 rounded-xl border border-[#BEBEBD]/50 shadow-[0_4px_24px_rgba(4,4,4,0.06)]">
          {status === "success" ? (
            <div
              data-testid="contact-success"
              className="flex flex-col items-center justify-center py-12 text-center"
            >
              <CheckCircle size={48} className="text-[#A7332B] mb-4" />
              <h3 className="font-heading text-2xl font-bold text-[#040404]">
                Message Sent!
              </h3>
              <p className="text-[#585857] mt-2 max-w-sm">
                Thank you for reaching out. We'll get back to you within 24
                hours.
              </p>
              <button
                data-testid="contact-send-another"
                onClick={() => setStatus("idle")}
                className="mt-6 text-[#A7332B] font-bold underline underline-offset-4 hover:text-[#040404] transition-colors"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label
                    htmlFor="name"
                    className="text-sm font-semibold text-[#040404]"
                  >
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                    className="bg-[#EDE9E1]/50 border-[#BEBEBD]/50 focus:border-[#A7332B] focus:ring-[#A7332B]/20 text-[#040404] placeholder:text-[#585857]/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="email"
                    className="text-sm font-semibold text-[#040404]"
                  >
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="contact-input-email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="you@company.com"
                    className="bg-[#EDE9E1]/50 border-[#BEBEBD]/50 focus:border-[#A7332B] focus:ring-[#A7332B]/20 text-[#040404] placeholder:text-[#585857]/50"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label
                    htmlFor="phone"
                    className="text-sm font-semibold text-[#040404]"
                  >
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+91 9876543210"
                    className="bg-[#EDE9E1]/50 border-[#BEBEBD]/50 focus:border-[#A7332B] focus:ring-[#A7332B]/20 text-[#040404] placeholder:text-[#585857]/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="company"
                    className="text-sm font-semibold text-[#040404]"
                  >
                    Company
                  </Label>
                  <Input
                    id="company"
                    name="company"
                    data-testid="contact-input-company"
                    value={form.company}
                    onChange={handleChange}
                    placeholder="Your company name"
                    className="bg-[#EDE9E1]/50 border-[#BEBEBD]/50 focus:border-[#A7332B] focus:ring-[#A7332B]/20 text-[#040404] placeholder:text-[#585857]/50"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="message"
                  className="text-sm font-semibold text-[#040404]"
                >
                  Message *
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Tell us about your requirements..."
                  className="bg-[#EDE9E1]/50 border-[#BEBEBD]/50 focus:border-[#A7332B] focus:ring-[#A7332B]/20 text-[#040404] placeholder:text-[#585857]/50 resize-none"
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit-btn"
                className="w-full flex items-center justify-center gap-2 bg-[#A7332B] text-[#EDE9E1] px-8 py-4 rounded-md font-bold tracking-wide hover:bg-[#040404] transition-colors"
              >
                <Send size={16} />
                Send Message
              </button>
            </form>
          )}
        </div>

        {/* Contact Info + Map */}
        <div className="flex flex-col gap-8 justify-center">
          {/* Contact details */}
          <div className="space-y-6">
            <a
              href="tel:+919911414981"
              data-testid="contact-phone"
              className="flex items-center gap-4 group"
            >
              <div className="w-14 h-14 bg-[#A7332B] rounded-xl flex items-center justify-center group-hover:bg-[#040404] transition-colors">
                <Phone size={20} className="text-[#EDE9E1]" />
              </div>
              <div>
                <p className="text-sm text-[#585857] font-medium">Call us</p>
                <p className="font-heading text-xl font-bold text-[#040404]">
                  +91 9911414981
                </p>
              </div>
            </a>

            <a
              href={`mailto:${COMPANY_EMAIL}`}
              data-testid="contact-email"
              className="flex items-center gap-4 group"
            >
              <div className="w-14 h-14 bg-[#A7332B] rounded-xl flex items-center justify-center group-hover:bg-[#040404] transition-colors">
                <Mail size={20} className="text-[#EDE9E1]" />
              </div>
              <div>
                <p className="text-sm text-[#585857] font-medium">Email us</p>
                <p className="font-heading text-lg font-bold text-[#040404]">
                  {COMPANY_EMAIL}
                </p>
              </div>
            </a>

            <div
              data-testid="contact-address"
              className="flex items-center gap-4"
            >
              <div className="w-14 h-14 bg-[#A7332B] rounded-xl flex items-center justify-center">
                <MapPin size={20} className="text-[#EDE9E1]" />
              </div>
              <div>
                <p className="text-sm text-[#585857] font-medium">Visit us</p>
                <p className="font-heading text-base font-bold text-[#040404] leading-snug">
                  GF, WZ 29 & 30, Gali No. 13<br />
                  Santgarh Extension, West Delhi 110018
                </p>
              </div>
            </div>
          </div>

          {/* Map */}
          <div
            data-testid="contact-map"
            className="w-full h-64 rounded-xl overflow-hidden border border-[#BEBEBD]/50 grayscale opacity-90 hover:grayscale-0 hover:opacity-100 transition-all duration-500"
          >
            <iframe
              title="Divya Enterprises Location"
              src="https://maps.google.com/maps?q=WZ+29+30+Gali+No+13+Santgarh+Extension+West+Delhi+110018&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
